import type { ScopeEditorAttributeDescriptor } from '../ui/components/ScopeEditorSection';

/**
 * Builds a `ScopeEditorAttributeDescriptor` array from the canonical
 * key order, default PCF labels, field-name map, and the incoming props
 * object.
 *
 * The field-name map (`fieldByRowId`) tells us the prop name that carries
 * the current row value (e.g. `pipingSpecValuePC`). The corresponding
 * change handler is derived by prefixing that name with `on` and suffixing
 * it with `Change` (e.g. `onPipingSpecValuePCChange`).
 */
export function buildScopeEditorAttributes<K extends string, TProps>(
  keys: readonly K[],
  defaultsMap: Record<K, string>,
  fieldByRowId: Record<K, string>,
  props: TProps
): ScopeEditorAttributeDescriptor[] {
  const recordProps = props as Record<string, unknown>;

  return keys.map((key) => {
    const valuePropName = fieldByRowId[key];
    const handlerPropName = `on${capitalizeFirst(valuePropName)}Change`;

    return {
      id: key,
      defaultLabel: defaultsMap[key],
      value: recordProps[valuePropName] as { value: string },
      onChange: recordProps[handlerPropName] as (value: string) => void,
    };
  });
}

function capitalizeFirst(value: string): string {
  return value ? `${value[0].toUpperCase()}${value.slice(1)}` : value;
}
